import { logger } from '@/utils/logger';
import { PlayerTrack } from '@/types/track_types';
import { tokenService } from '@/utils/tokenService';

/**
 * 스트리밍 URL 생성 함수
 * 트랙 번호로 인증 토큰이 포함된 스트리밍 URL 생성
 *
 * HTML5 Audio 요소는 커스텀 헤더를 설정할 수 없으므로
 * JWT 토큰을 쿼리 파라미터로 전달
 *
 * @param trackNumber - 트랙 번호
 * @returns 인증 토큰이 포함된 스트리밍 URL
 */
export const getStreamingUrl = (trackNumber: number): string => {
  const baseUrl = `${import.meta.env.VITE_API_URL}/tracks/${trackNumber}/stream/`;
  const accessToken = tokenService.getAccessToken();

  if (accessToken) {
    return `${baseUrl}?token=${encodeURIComponent(accessToken)}`;
  }

  return baseUrl;
};

/**
 * 오디오 URL 반환 함수
 * 백엔드 스트리밍 API에서 인증 기반으로 구매 여부를 확인하므로
 * 프론트엔드에서는 단순히 audioUrl을 반환
 */
export const getAudioUrl = (track: PlayerTrack): string => {
  return track.audioUrl;
};

/**
 * duration을 초 단위 숫자로 변환
 * @param duration - "HH:MM:SS" 또는 "MM:SS" 문자열, 또는 숫자(초)
 * @returns 초 단위 숫자
 */
export const parseDuration = (duration: number | string | undefined): number => {
  if (duration === undefined || duration === null) return 0;
  if (typeof duration === 'number') return duration;

  // "HH:MM:SS" 또는 "MM:SS" 형식 파싱
  const parts = duration.split(':').map(Number);
  if (parts.some(isNaN)) return 0;

  if (parts.length === 3) {
    // HH:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

/**
 * 시간을 MM:SS 형식으로 포맷팅
 */
export const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Seek 에러 감지 함수
 * 오디오 에러가 seeking 관련 에러인지 확인
 */
export const isSeekError = (error: MediaError | null): boolean => {
  return error?.message?.includes('seek failed') ?? false;
};

/**
 * 오디오 에러 로깅 헬퍼
 */
export const logAudioError = (audio: HTMLAudioElement): void => {
  logger.error('Audio Error:', {
    error: audio.error,
    code: audio.error?.code,
    message: audio.error?.message,
    src: audio.src,
    currentTime: audio.currentTime,
  });
};

/**
 * 트랙 정보 로깅 헬퍼 (개발 환경에서만)
 */
export const logTrackInfo = (track: PlayerTrack, isPurchased: boolean, audioUrl: string): void => {
  if (import.meta.env.NODE_ENV === 'development') {
    logger.debug('Playing track:', {
      id: track.id,
      title: track.title,
      isPurchased,
      audioUrl,
    });
  }
};

/**
 * 메타데이터 로딩 로그 헬퍼
 */
export const logMetadata = (duration: number): void => {
  if (import.meta.env.NODE_ENV === 'development') {
    logger.debug('Loaded metadata:', { duration });
  }
};
